/*
 * Saved pieces (naomi-ops docs/superpowers/specs/2026-10-04-saved-pieces-design.md).
 *
 * A save is one product in one colour. Its key is `handle|color` (color is the variant's
 * colour option value, lowercased, from snippets/variant-colour-value), or just `handle`
 * for a product without a colour option. Each save keeps the variant it was made on.
 *
 * <naomi-save-button data-handle data-color data-variant-id data-title data-surface>
 * wraps one <button aria-pressed>; snippets/naomi-save-button.liquid renders it with both
 * localised labels as data-label-save / data-label-remove. Buttons with the same key sync;
 * the colours of one product toggle independently. PDP buttons (data-colours) follow the
 * selected variant's colour.
 *
 * Two sources of truth, never mixed:
 * - Signed out: localStorage 'naomi:saved', [{ handle, color, variantId, savedAt }] newest
 *   first. Corrupt or blocked storage reads as empty (entries from before colours, with no
 *   color or variantId, are dropped); a toggle still works for the page view.
 * - Signed in: layout/theme.liquid prints #naomi-saved-account ([{ handle, color, variantId }]
 *   from customer.metafields.naomi.saved). Toggles are optimistic, then POST
 *   /apps/naomi/saved { op, ids: [ProductVariant GIDs] } (app proxy to naomi-admin). POSTs
 *   are serialised per key and each sends the state the button shows at send time, so the
 *   last tap wins; a remove sends the variant the account holds for that key. Only a failed
 *   final request reverts. A non-empty guest list (100 newest) is merged into the account
 *   once; it is cleared on any 2xx or 4xx and kept only for a network error or a 5xx.
 *
 * Every change dispatches 'naomi:saved-change' on document. The pure list functions
 * live in NaomiSavedLib so tests/naomi-save.test.mjs can load this file in a vm context.
 */
(function (root) {
  const KEY = 'naomi:saved';

  const keyOf = ({ handle, color }) => (color ? `${handle}|${color}` : handle);
  const parse = (raw) => {
    try {
      const value = JSON.parse(raw);
      if (!Array.isArray(value)) return [];
      const seen = new Set();
      return value.filter((entry) => {
        if (!entry || typeof entry.handle !== 'string' || !entry.handle) return false;
        if (typeof entry.color !== 'string' || !entry.variantId) return false; // pre-colour entries
        const key = keyOf(entry);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    } catch {
      return [];
    }
  };
  const find = (list, key) => list.find((entry) => keyOf(entry) === key);
  const has = (list, key) => !!find(list, key);
  const remove = (list, key) => list.filter((entry) => keyOf(entry) !== key);
  const add = (list, { handle, color, variantId }, savedAt) => [
    { handle, color, variantId, savedAt },
    ...remove(list, keyOf({ handle, color })),
  ];
  const toggle = (list, item, savedAt) => (has(list, keyOf(item)) ? remove(list, keyOf(item)) : add(list, item, savedAt));
  const gid = (id) => `gid://shopify/ProductVariant/${id}`;
  // The endpoint takes at most 100 ids; the list is newest first, so the newest 100 go.
  // A colour the account already holds is skipped, so one colour never gets two variants.
  const mergePayload = (list, account = []) => {
    const held = new Set(account.map(keyOf));
    return { op: 'merge', ids: list.filter((entry) => !held.has(keyOf(entry))).slice(0, 100).map((entry) => gid(entry.variantId)) };
  };
  // Every variant id the account holds per key (older data can hold two variants of one
  // colour); a remove sends them all.
  const idsByKey = (entries) => {
    const byKey = new Map();
    entries.forEach((entry) => byKey.set(keyOf(entry), [...(byKey.get(keyOf(entry)) || []), entry.variantId]));
    return byKey;
  };
  // The endpoint answers with variant GIDs only: map them back onto entries this page
  // knows. A variant saved elsewhere since this page rendered is skipped here and arrives
  // with the next server render.
  const resolveIds = (ids, known) => {
    const byId = new Map(known.map((entry) => [gid(entry.variantId), entry]));
    return ids.map((id) => byId.get(id)).filter(Boolean);
  };
  const fromIds = (ids, known) => parse(JSON.stringify(resolveIds(ids, known)));
  // The body for one key at send time: add the variant the list holds, remove every
  // variant the account holds for that key (which may differ from the button's variant).
  const syncBody = (list, key, accountVariantIds = []) => {
    const entry = find(list, key);
    return entry ? { op: 'add', ids: [gid(entry.variantId)] } : { op: 'remove', ids: accountVariantIds.map(gid) };
  };
  // Guest Saved page: a fetched card can stand for another key than the stored entry
  // (variant deleted, colour renamed, product gained a colour). The entry takes the card's
  // key and variant; when that key is already stored, the entry goes.
  const rekey = (list, oldKey, card) => {
    const entry = find(list, oldKey);
    if (!entry || keyOf(card) === oldKey) return list;
    if (has(list, keyOf(card))) return remove(list, oldKey);
    return list.map((e) => (e === entry ? { ...card, savedAt: entry.savedAt } : e));
  };

  root.NaomiSavedLib = { KEY, keyOf, parse, has, find, add, remove, toggle, gid, mergePayload, idsByKey, fromIds, resolveIds, syncBody, rekey };
  if (typeof document === 'undefined' || !root.customElements) return; // node test: pure functions only

  const accountEl = document.getElementById('naomi-saved-account');
  const signedIn = !!accountEl;
  const now = () => new Date().toISOString();
  const itemOf = (el) => ({ handle: el.dataset.handle, color: el.dataset.color || '', variantId: Number(el.dataset.variantId) });

  const readGuest = () => {
    try {
      return parse(localStorage.getItem(KEY));
    } catch {
      return [];
    }
  };
  const writeGuest = (next) => {
    try {
      if (next.length) localStorage.setItem(KEY, JSON.stringify(next));
      else localStorage.removeItem(KEY);
    } catch {
      // blocked or full: the in-memory list still carries this page view
    }
  };

  const readAccount = () => {
    try {
      const raw = JSON.parse(accountEl.textContent);
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  };
  const accountRaw = signedIn ? readAccount() : [];
  let list = signedIn ? parse(JSON.stringify(accountRaw)) : readGuest();
  if (!signedIn) writeGuest(list); // drops pre-colour entries for good

  const render = () => {
    document.querySelectorAll('naomi-save-button').forEach((el) => el.render?.());
    document.querySelectorAll('[data-naomi-saved-link]').forEach((link) => {
      link.hidden = !list.length;
    });
    document.dispatchEvent(new CustomEvent('naomi:saved-change', { detail: { list } }));
  };

  const set = (next) => {
    list = next;
    if (!signedIn) writeGuest(next);
    render();
  };

  // Same gate as naomi-pdp-track.js: nothing is sent or queued before consent has loaded posthog.
  const track = (event, props) => {
    if (window.NaomiPostHog?.ready) window.posthog?.capture(event, props);
  };

  // Resolves to the Response (any status); rejects only on a network error.
  const post = (body) =>
    fetch('/apps/naomi/saved', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

  // Signed in, per key: every variant id the server last confirmed (absent when not saved),
  // the request chain, and how many requests are queued or in flight.
  let confirmed = idsByKey(accountRaw.filter((e) => e && e.handle && e.variantId && typeof e.color === 'string'));
  const chains = new Map();
  const queued = new Map();
  const pending = (key) => (queued.get(key) || 0) > 0;

  const sync = (item, surface) => {
    const key = keyOf(item);
    queued.set(key, (queued.get(key) || 0) + 1);
    const send = async () => {
      const want = has(list, key); // what the button shows now: the last tap wins
      let ok = true;
      if (want !== confirmed.has(key)) {
        const body = syncBody(list, key, confirmed.get(key));
        try {
          ok = (await post(body)).ok;
        } catch {
          ok = false;
        }
        if (ok) {
          if (want) confirmed.set(key, [find(list, key)?.variantId ?? item.variantId]);
          else confirmed.delete(key);
          track(want ? 'item_saved' : 'item_unsaved', { handle: item.handle, color: item.color, surface });
        }
      }
      queued.set(key, queued.get(key) - 1);
      // Only the final request decides a revert, back to what the server last confirmed.
      if (!ok && !pending(key) && has(list, key) !== confirmed.has(key)) {
        set(confirmed.has(key) ? add(list, { ...item, variantId: confirmed.get(key)[0] }, now()) : remove(list, key));
      } else {
        render(); // lets the Saved page drop a card it was holding hidden
      }
    };
    chains.set(key, (chains.get(key) || Promise.resolve()).then(send));
  };

  // The save toast (NAO-102): one element, made once, on every page but the Saved page
  // (where a tap can only remove). A save fills it and restarts its clock; a remove stays
  // silent. Strings and URLs come from #naomi-saved-toast (the layouts). The visible toast
  // is an ink bar (full bleed on phones, a block at the bottom left from 990px); it is never
  // focused, and a persistent polite status region carries the announcement.
  let toastData = null;
  try {
    toastData = JSON.parse(document.getElementById('naomi-saved-toast')?.textContent || 'null');
  } catch {}
  let toast = null;
  let live = null;
  let toastKey = null;
  let toastFrom = null;
  let toastTimer;
  const armToast = () => {
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 4000);
  };
  const hideToast = () => {
    clearTimeout(toastTimer);
    toastKey = null;
    if (toast.contains(document.activeElement)) toastFrom?.focus();
    toast.classList.remove('is-on');
  };
  const showToast = (el) => {
    if (!toast) return;
    toastKey = keyOf(itemOf(el));
    toastFrom = el.button;
    const title = el.dataset.title || '';
    toast.querySelector('.naomi-toast__title').textContent = title;
    document.body.style.setProperty('--naomi-toast-h', `${toast.offsetHeight}px`); // lifts the chat bubble (naomi-save.css)
    toast.classList.add('is-on');
    const guest = toast.querySelector('.naomi-toast__guest');
    live.textContent = '';
    setTimeout(() => (live.textContent = `${toastData.saved}. ${title}${guest ? ` ${guest.textContent}` : ''}`), 100);
    armToast();
  };
  if (toastData && !document.querySelector('[data-naomi-saved-page]')) {
    live = document.createElement('div');
    live.className = 'visually-hidden';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    toast = document.createElement('div');
    toast.className = 'naomi-toast';
    toast.innerHTML = `<span class="naomi-toast__saved"></span><span class="naomi-toast__title"></span><a class="naomi-toast__view"></a><button type="button" class="naomi-toast__close"><span class="visually-hidden"></span><svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M1 1l10 10M11 1L1 11" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="butt"/></svg></button>${signedIn ? '' : '<p class="naomi-toast__guest"></p>'}`;
    toast.querySelector('.naomi-toast__saved').textContent = toastData.saved;
    const view = toast.querySelector('.naomi-toast__view');
    view.href = toastData.url;
    view.textContent = toastData.view;
    toast.querySelector('.naomi-toast__close .visually-hidden').textContent = toastData.close;
    if (!signedIn) toast.querySelector('.naomi-toast__guest').innerHTML = toastData.guest; // theme locale _html
    toast.querySelector('.naomi-toast__close').addEventListener('click', hideToast);
    toast.addEventListener('mouseenter', () => clearTimeout(toastTimer));
    toast.addEventListener('mouseleave', () => toastKey && !toast.contains(document.activeElement) && armToast());
    toast.addEventListener('focusin', () => clearTimeout(toastTimer));
    toast.addEventListener('focusout', (event) => toastKey && !toast.contains(event.relatedTarget) && !toast.matches(':hover') && armToast());
    document.addEventListener('keydown', (event) => event.key === 'Escape' && toastKey && hideToast());
    // A signed-in save the server rejected reverts: the toast goes with it.
    document.addEventListener('naomi:saved-change', () => toastKey && !has(list, toastKey) && hideToast());
    document.body.append(live, toast);
  }

  const flip = (el) => {
    const item = itemOf(el);
    const wasSaved = has(list, keyOf(item));
    set(wasSaved ? remove(list, keyOf(item)) : add(list, item, now()));
    if (!wasSaved) showToast(el);
    if (signedIn) return sync(item, el.dataset.surface);
    track(wasSaved ? 'item_unsaved' : 'item_saved', { handle: item.handle, color: item.color, surface: el.dataset.surface });
  };

  class NaomiSaveButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      if (!this.wired) {
        this.wired = true;
        this.button.addEventListener('click', (event) => {
          // Cards sit under a stretched link: the bookmark never navigates.
          event.preventDefault();
          event.stopPropagation();
          flip(this);
        });
      }
      this.render();
    }

    render() {
      if (!this.button) return;
      const saved = has(list, keyOf(itemOf(this)));
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const label = saved ? this.dataset.labelRemove : this.dataset.labelSave;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }
  if (!customElements.get('naomi-save-button')) customElements.define('naomi-save-button', NaomiSaveButton);

  // PDP: the bookmarks follow the selected variant. A new colour is a new key; a new length
  // or karat in the same colour keeps the key, so the state stays.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant;
      if (!variant) return;
      document
        .querySelectorAll(`#shopify-section-${event.data.sectionId} naomi-save-button[data-colours]`)
        .forEach((el) => {
          let colours = {};
          try {
            colours = JSON.parse(el.dataset.colours);
          } catch {}
          const value = (variant.options || []).find((option) => option in colours);
          el.dataset.variantId = variant.id;
          if (value !== undefined) el.dataset.color = colours[value];
          el.render();
        });
    });
  }

  // Other tabs (guest only; the account list is per page view by design).
  window.addEventListener('storage', (event) => {
    if (signedIn || event.key !== KEY) return;
    list = parse(event.newValue);
    render();
  });

  // Sign-in merge: the guest list joins the account once, then leaves this device. A 4xx
  // will never succeed on retry, so it clears too; a network error or a 5xx keeps it.
  if (signedIn) {
    const guest = readGuest();
    const payload = mergePayload(guest, list);
    if (!guest.length) {
      // nothing to merge
    } else if (!payload.ids.length) {
      writeGuest([]); // every guest colour is already on the account
    } else {
      post(payload)
        .then(async (res) => {
          if (res.status >= 500) return;
          writeGuest([]);
          if (!res.ok) return;
          const body = await res.json().catch(() => null);
          if (!Array.isArray(body?.ids)) return;
          const known = [...accountRaw, ...guest];
          set(fromIds(body.ids, known));
          confirmed = idsByKey(resolveIds(body.ids, known));
        })
        .catch(() => {});
    }
  }

  // The Saved page (sections/naomi-saved.liquid), one card per saved colour. Signed in, the
  // cards are server rendered; as a guest each card comes from
  // /products/<handle>?variant=<id>&view=card (templates/product.card.liquid).
  const page = document.querySelector('[data-naomi-saved-page]');
  if (page) {
    const grid = page.querySelector('[data-naomi-saved-grid]');
    const empty = page.querySelector('[data-naomi-saved-empty]');
    // aria-busy (guest, while the cards load) keeps the empty line from flashing first.
    const showEmpty = () => {
      if (!page.hasAttribute('aria-busy')) empty.hidden = !!grid.querySelector(':scope > li:not([hidden])');
    };

    // Unsaving here takes the card away in place. Signed in, the card is only hidden until
    // the server confirms (removed then), and comes back if the request fails.
    document.addEventListener('naomi:saved-change', () => {
      grid.querySelectorAll(':scope > li').forEach((li) => {
        const button = li.querySelector('naomi-save-button');
        if (!button) return;
        const key = keyOf(itemOf(button));
        const saved = has(list, key);
        if (!saved && (!signedIn || (!pending(key) && !confirmed.has(key)))) li.remove();
        else li.hidden = !saved;
      });
      showEmpty();
    });

    if (page.hasAttribute('data-server')) {
      showEmpty();
    } else {
      const rootPath = window.Shopify?.routes?.root || '/';
      const wanted = list.slice(0, 48);
      Promise.all(
        wanted.map((entry) =>
          fetch(`${rootPath}products/${encodeURIComponent(entry.handle)}?variant=${entry.variantId}&view=card`)
            .then((res) => {
              if (res.status === 404) return { gone: keyOf(entry) };
              return res.ok ? res.text() : '';
            })
            .catch(() => '')
        )
      ).then((results) => {
        let next = list;
        const shown = new Set();
        results.forEach((result, i) => {
          const key = keyOf(wanted[i]);
          if (result && result.gone) return (next = remove(next, key));
          if (!result) return;
          const li = document.createElement('li');
          li.className = 'grid__item';
          li.innerHTML = result;
          const button = li.querySelector('naomi-save-button');
          if (button) {
            const card = itemOf(button);
            next = rekey(next, key, card);
            if (shown.has(keyOf(card))) return; // two saves that turn out to be one colour: one card
            shown.add(keyOf(card));
          }
          grid.appendChild(li);
        });
        if (next !== list) set(parse(JSON.stringify(next))); // parse drops a key stored twice
        page.removeAttribute('aria-busy');
        showEmpty();
      });
    }
  }

  render();
})(globalThis);
